/**
 * Photovoltaik & Mieterstrom in der Betriebskostenabrechnung.
 *
 * Rechtsstand geprüft 15.09.2026. Reiner Rechenkern (Regel 5), Geld in Cent, integer (Regel 6).
 * Jede Regel trägt ihren Rechtsstand (Regel 8); es wird keine Regel der Technik als Gesetz
 * ausgegeben (Regel 9), und wo die Behandlung angreifbar ist, wird darauf hingewiesen statt sie
 * als sicher auszugeben (Regel 10). Kein Rechtsrat im Text (Regel 7) — nur allgemeine Einordnung.
 *
 * Grundsatz: Betriebskosten sind die dem Vermieter TATSÄCHLICH entstandenen laufenden Kosten
 * (§ 556 Abs. 1 BGB, § 1 BetrKV). Für den Allgemeinstrom (Beleuchtung, § 2 Nr. 11 BetrKV) ist
 * deshalb nur der tatsächlich bezogene und bezahlte Fremdstrom umlagefähig. Selbst erzeugter,
 * im Allgemeinstrom verbrauchter PV-Strom verursacht keine externen Kosten und darf nicht zu
 * einem fiktiven Markt- oder Arbeitspreis auf die Mieter umgelegt werden. Er senkt die
 * Fremdstromrechnung ohnehin bereits — eine zusätzliche Umlage wäre eine doppelte, fiktive
 * Position.
 *
 * Mieterstrom (Belieferung der Mietparteien mit Strom aus der Anlage) ist ein eigenständiger
 * Stromliefervertrag nach EnWG und KEINE Betriebskostenposition. Er wird getrennt abgerechnet
 * und gehört nicht in die Nebenkostenabrechnung. Der Mieterstromzuschlag (§ 21 Abs. 3 EEG) steht
 * dem Anlagenbetreiber zu, nicht den Mietparteien.
 */

export const PV_RECHTSSTAND = '2026-09-15';

/** Betreibermodell der PV-Anlage — bestimmt, ob und wie etwas in die Abrechnung fließt. */
export type Betreibermodell = 'allgemeinstrom_eigenverbrauch' | 'mieterstrom' | 'volleinspeisung';

export interface AllgemeinstromEingabe {
  /** Tatsächlich bezahlte Fremdstromrechnung für den Allgemeinstrom, in Cent. */
  externeStromkostenCent: number;
  /** Im Allgemeinstrom selbst verbrauchter PV-Strom in kWh (optional, nur Ausweis/Transparenz). */
  pvEigenverbrauchKwh?: number;
  /** Arbeitspreis in Cent/kWh — nur zur Information über die dadurch eingesparten Fremdkosten. */
  arbeitspreisCentProKwh?: number;
}

export interface AllgemeinstromErgebnis {
  /** Umlagefähiger Betrag = ausschließlich der tatsächlich bezahlte Fremdstrom, in Cent. */
  umlagefaehigCent: number;
  /** Übernommener PV-Eigenverbrauch (kWh), nur ausgewiesen. */
  pvEigenverbrauchKwh: number;
  /** Rechnerisch durch PV-Eigenverbrauch eingesparte Fremdkosten in Cent, nur Ausweis. */
  ersparnisCent: number;
  hinweise: string[];
}

/**
 * Ermittelt den umlagefähigen Allgemeinstrom bei vorhandener PV-Anlage.
 *
 * Umgelegt wird nur der tatsächlich bezahlte Fremdstrom. Der PV-Eigenverbrauch wird nicht
 * hinzugerechnet; er wird lediglich zur Transparenz ausgewiesen (eingesparte Fremdkosten).
 */
export function allgemeinstromUmlage(e: AllgemeinstromEingabe): AllgemeinstromErgebnis {
  const extern = Math.max(0, Math.round(e.externeStromkostenCent));
  const kwh = Math.max(0, e.pvEigenverbrauchKwh ?? 0);
  const preis = Math.max(0, e.arbeitspreisCentProKwh ?? 0);
  const ersparnisCent = Math.round(kwh * preis);

  const hinweise: string[] = [];
  if (kwh > 0) {
    hinweise.push(
      'Selbst erzeugter PV-Strom im Allgemeinstrom ist nicht umlagefähig und wird nicht auf die ' +
        'Mietparteien umgelegt. Umgelegt wird nur der tatsächlich bezahlte Fremdstrom; der ' +
        'PV-Anteil senkt die Fremdstromrechnung bereits.',
    );
  }

  return { umlagefaehigCent: extern, pvEigenverbrauchKwh: kwh, ersparnisCent, hinweise };
}

export const MIETERSTROM_HINWEIS =
  'Mieterstrom ist ein Stromliefervertrag (EnWG) und keine Betriebskostenposition. Er wird ' +
  'getrennt abgerechnet und darf nicht in die Nebenkostenabrechnung aufgenommen werden.';

export interface BetreibermodellEinordnung {
  /** Erzeugt dieses Modell überhaupt eine Position in der Betriebskostenabrechnung? */
  erzeugtBetriebskosten: boolean;
  hinweise: string[];
}

/** Ordnet ein Betreibermodell für die Abrechnung ein und liefert die passenden Hinweise. */
export function betreibermodellEinordnung(modell: Betreibermodell): BetreibermodellEinordnung {
  switch (modell) {
    case 'allgemeinstrom_eigenverbrauch':
      return {
        erzeugtBetriebskosten: true,
        hinweise: [
          'Die PV-Anlage deckt (teilweise) den Allgemeinstrom. Umlagefähig ist nur der tatsächlich ' +
            'bezahlte Fremdstrom, nicht der PV-Eigenverbrauch.',
        ],
      };
    case 'mieterstrom':
      return { erzeugtBetriebskosten: false, hinweise: [MIETERSTROM_HINWEIS] };
    case 'volleinspeisung':
      return {
        erzeugtBetriebskosten: false,
        hinweise: [
          'Bei Volleinspeisung wird der gesamte PV-Strom ins Netz eingespeist. Es entsteht keine ' +
            'Betriebskostenposition; der Allgemeinstrom bleibt vollständig Fremdbezug.',
        ],
      };
  }
}
